/** Patient chronic condition presets and normalization (aligned with CCM care plan conditions). */

import { parseRawChronicConditionItems } from './patientBillingUtils';

export const CHRONIC_CONDITION_PRESETS = [
  "Alzheimer's Disease/Dementia",
  'Arthritis (Rheumatoid)',
  'Arthritis (Osteoarthritis)',
  'Asthma',
  'Atrial Fibrillation',
  'Autism Spectrum Disorders',
  'Cancer (Active)',
  'Cardiovascular Disease',
  'Chronic Heart Failure (CHF)',
  'Chronic Kidney Disease (CKD)',
  'Chronic Obstructive Pulmonary Disease (COPD)',
  "Crohn's Disease",
  'Depression',
  'Diabetes Mellitus Type 1',
  'Diabetes Mellitus Type 2',
  'Hepatitis (Chronic)',
  'HIV/AIDS',
  'Hypertension',
  'Hyperlipidemia',
  'Ischemic Heart Disease',
  'Multiple Sclerosis',
  'Obesity',
  'Osteoporosis',
  "Parkinson's Disease",
  'Psoriasis',
  'Schizophrenia/Psychotic Disorders',
  'Stroke (History of)',
  'Substance Use Disorder',
  'Thyroid Disease',
  'Ulcerative Colitis',
];

export const DEFAULT_ICD_BY_CONDITION = {
  "Alzheimer's Disease/Dementia": 'F03.90',
  'Arthritis (Rheumatoid)': 'M06.9',
  'Arthritis (Osteoarthritis)': 'M19.90',
  Asthma: 'J45.909',
  'Atrial Fibrillation': 'I48.91',
  'Autism Spectrum Disorders': 'F84.0',
  'Cancer (Active)': 'C80.1',
  'Cardiovascular Disease': 'I25.10',
  'Chronic Heart Failure (CHF)': 'I50.9',
  'Chronic Kidney Disease (CKD)': 'N18.3',
  'Chronic Obstructive Pulmonary Disease (COPD)': 'J44.9',
  "Crohn's Disease": 'K50.90',
  Depression: 'F32.1',
  'Diabetes Mellitus Type 1': 'E10.9',
  'Diabetes Mellitus Type 2': 'E11.9',
  'Hepatitis (Chronic)': 'B18.2',
  'HIV/AIDS': 'B20',
  Hypertension: 'I10',
  Hyperlipidemia: 'E78.5',
  'Ischemic Heart Disease': 'I25.10',
  'Multiple Sclerosis': 'G35',
  Obesity: 'E66.9',
  Osteoporosis: 'M81.0',
  "Parkinson's Disease": 'G20',
  Psoriasis: 'L40.9',
  'Schizophrenia/Psychotic Disorders': 'F20.9',
  'Stroke (History of)': 'Z86.73',
  'Substance Use Disorder': 'F19.10',
  'Thyroid Disease': 'E03.9',
  'Ulcerative Colitis': 'K51.90',
};

const normalizeIcd = (code) => String(code || '').trim().toUpperCase().replace(/\s+/g, '');

/** Default ICD-10 for a known chronic condition preset name. */
export function getDefaultIcdForCondition(conditionName) {
  const name = String(conditionName || '').trim();
  if (!name) return '';
  if (DEFAULT_ICD_BY_CONDITION[name]) return DEFAULT_ICD_BY_CONDITION[name];
  const lower = name.toLowerCase();
  const match = Object.keys(DEFAULT_ICD_BY_CONDITION).find((k) => k.toLowerCase() === lower);
  return match ? DEFAULT_ICD_BY_CONDITION[match] : '';
}

/**
 * Resolve condition name from an ICD code (exact or prefix match against presets).
 * Prefers exact match, then longest matching prefix (min 3 chars for fuzzy).
 */
export function getConditionNameForIcd(icdCode) {
  const code = normalizeIcd(icdCode);
  if (!code) return '';

  for (const [name, defaultIcd] of Object.entries(DEFAULT_ICD_BY_CONDITION)) {
    if (normalizeIcd(defaultIcd) === code) return name;
  }

  if (code.length < 3) return '';

  let bestName = '';
  let bestLen = 0;
  for (const [name, defaultIcd] of Object.entries(DEFAULT_ICD_BY_CONDITION)) {
    const preset = normalizeIcd(defaultIcd);
    if (!preset) continue;
    if (code.startsWith(preset) || preset.startsWith(code)) {
      const overlap = Math.min(code.length, preset.length);
      if (overlap > bestLen) {
        bestLen = overlap;
        bestName = name;
      }
    }
  }
  return bestName;
}

export const EMPTY_PATIENT_CHRONIC_CONDITION = {
  icd_code: '',
  condition_name: '',
  severity: 'moderate',
  onset_date: '',
  status: 'active',
  billing_role: 'additional',
};

export function createPatientChronicConditionFromPreset(presetName) {
  return {
    ...EMPTY_PATIENT_CHRONIC_CONDITION,
    condition_name: presetName,
    icd_code: getDefaultIcdForCondition(presetName),
  };
}

export function normalizePatientChronicCondition(entry) {
  if (entry == null || entry === '') return null;
  if (typeof entry === 'string') {
    const trimmed = entry.trim();
    if (!trimmed) return null;
    const codeNameMatch = trimmed.match(/^([A-Z]\d[\w.]*)\s*[-–:]\s*(.+)$/i);
    if (codeNameMatch) {
      return {
        ...EMPTY_PATIENT_CHRONIC_CONDITION,
        icd_code: codeNameMatch[1].trim(),
        condition_name: codeNameMatch[2].trim(),
      };
    }
    const condition_name = trimmed;
    return {
      ...EMPTY_PATIENT_CHRONIC_CONDITION,
      condition_name,
      icd_code: getDefaultIcdForCondition(condition_name),
    };
  }
  if (typeof entry === 'object') {
    const condition_name = String(
      entry.condition_name || entry.icd_description || entry.description || '',
    ).trim();
    if (!condition_name) return null;
    return {
      icd_code: String(entry.icd_code || entry.code || '').trim() || getDefaultIcdForCondition(condition_name),
      condition_name,
      severity: entry.severity || 'moderate',
      onset_date: entry.onset_date ? String(entry.onset_date).slice(0, 10) : '',
      status: entry.status || 'active',
      billing_role: entry.billing_role || 'additional',
    };
  }
  return null;
}

export function normalizePatientChronicConditions(raw) {
  if (raw == null || raw === '') return [];
  return parseRawChronicConditionItems(raw).map(normalizePatientChronicCondition).filter(Boolean);
}

export function getPatientChronicConditionKey(condition) {
  return String(condition?.condition_name || '').trim().toLowerCase();
}

export function serializePatientChronicConditions(conditions = []) {
  return conditions.map((c) => ({
    icd_code: c.icd_code?.trim() || null,
    condition_name: c.condition_name.trim(),
    severity: c.severity || 'moderate',
    onset_date: c.onset_date || null,
    status: c.status || 'active',
    billing_role: c.billing_role || 'additional',
  }));
}

export function formatPatientChronicConditionSummary(condition) {
  const parts = [];
  if (condition.icd_code) parts.push(`ICD ${condition.icd_code}`);
  if (condition.severity) parts.push(condition.severity);
  if (condition.status) parts.push(condition.status);
  if (condition.onset_date) parts.push(`Date of diagnosis ${condition.onset_date}`);
  if (condition.billing_role) parts.push(condition.billing_role);
  return parts.join(' · ');
}
